import React, { useState, useEffect } from 'react';
import Layout from '../../Layout/Layout';
import { useNavigate } from 'react-router-dom';
import { HexColorPicker } from 'react-colorful';
import Swal from 'sweetalert2';
import { 
  FaHome, FaCar, FaUtensils, FaShoppingCart, FaPlane, 
  FaGraduationCap, FaGift, FaMoneyBillWave,
  FaPiggyBank, FaChartLine, FaWallet, FaCreditCard,
  FaTimes, FaPlus, FaSave, FaSpinner, FaExclamationTriangle
} from 'react-icons/fa';
import './CreateTransactionCategory.css';

// API Configuration
const API_BASE_URL = 'http://localhost:8000/api';
const REFRESH_TOKEN_ENDPOINT = '/auth/refresh/';

// SweetAlert2 Configuration
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
});

const CreateTransactionCategory = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_type: 'expense',
    icon: 'FaWallet',
    color: '#1a237e'
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [tokenExpired, setTokenExpired] = useState(false);


  // Alert Functions
  const showSuccessAlert = (message) => {
    Toast.fire({
      icon: 'success',
      title: message
    });
  };

  const showErrorAlert = (message) => {
    Toast.fire({
      icon: 'error',
      title: message
    });
  };

  const showConfirmDialog = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1a237e',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, create it!',
      cancelButtonText: 'Cancel'
    });
    return result.isConfirmed;
  };

  const showSessionExpiredAlert = () => {
    Swal.fire({
      title: 'Session Expired',
      text: 'Your session has expired. Please log in again.',
      icon: 'warning',
      confirmButtonColor: '#1a237e',
      confirmButtonText: 'Go to Login',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/login');
      }
    });
  };

  const categoryTypes = [
    { value: 'income', label: 'Income' },
    { value: 'expense', label: 'Expense' },
    { value: 'investment', label: 'Investment' },
    { value: 'savings', label: 'Savings' }
  ];

  const icons = [
    { value: 'FaHome', icon: <FaHome />, label: 'Home' },
    { value: 'FaCar', icon: <FaCar />, label: 'Transport' },
    { value: 'FaUtensils', icon: <FaUtensils />, label: 'Food' },
    { value: 'FaShoppingCart', icon: <FaShoppingCart />, label: 'Shopping' },
    { value: 'FaPlane', icon: <FaPlane />, label: 'Travel' },
    { value: 'FaGraduationCap', icon: <FaGraduationCap />, label: 'Education' },
    { value: 'FaGift', icon: <FaGift />, label: 'Gifts' },
    { value: 'FaMoneyBillWave', icon: <FaMoneyBillWave />, label: 'Income' },
    { value: 'FaPiggyBank', icon: <FaPiggyBank />, label: 'Savings' },
    { value: 'FaChartLine', icon: <FaChartLine />, label: 'Investment' },
    { value: 'FaWallet', icon: <FaWallet />, label: 'Wallet' },
    { value: 'FaCreditCard', icon: <FaCreditCard />, label: 'Credit Card' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    // Clear API error when form is edited
    if (apiError) {
      setApiError('');
    }
  };

  const handleColorChange = (color) => {
    setFormData(prev => ({
      ...prev,
      color
    }));
  };

  const handleIconSelect = (iconValue) => {
    setFormData(prev => ({
      ...prev,
      icon: iconValue
    }));
    setShowIconPicker(false);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await fetch(`${API_BASE_URL}${REFRESH_TOKEN_ENDPOINT}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken })
      });

      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }

      const data = await response.json();
      localStorage.setItem('authToken', data.access);
      return data.access;
    } catch (error) {
      console.error('Token refresh failed:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      setTokenExpired(true);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setApiError('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found. Please log in.');
      }

      const submitData = async (token) => {
        const payload = {
          name: formData.name.trim(),
          description: formData.description.trim(),
          category_type: formData.category_type,
          icon: formData.icon,
          color: formData.color,
        };

        console.log('Submitting category data:', payload);

        const response = await fetch(`${API_BASE_URL}/categories/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        console.log('Response status:', response.status);
        console.log('Response headers:', Object.fromEntries(response.headers.entries()));

        if (response.status === 401) {
          const newToken = await refreshToken();
          return submitData(newToken);
        }

        const responseData = await response.json();
        
        if (!response.ok) {
          console.error('API Error Response:', responseData);
          throw new Error(
            responseData.message || 
            responseData.detail || 
            responseData.error || 
            'Failed to create category'
          );
        }

        return responseData;
      };

      const data = await submitData(token);
      console.log('Category created successfully:', data);
      
      // Show success message
      showSuccessAlert('Category created successfully!');
      
      // Navigate after a short delay
      setTimeout(() => {
        navigate('/transaction-categories');
      }, 1500);

    } catch (error) {
      console.log('Full error object:', error);
      console.error('Error creating category:', error);
      
      if (error.message.includes('token')) {
        setTokenExpired(true);
        showSessionExpiredAlert();
      } else if (error.message.includes('400')) {
        showErrorAlert('Invalid data provided. Please check your input and try again.');
      } else if (error.message.includes('403')) {
        showErrorAlert('You do not have permission to create categories.');
      } else if (error.message.includes('404')) {
        showErrorAlert('The category endpoint was not found.');
      } else if (error.message.includes('500')) {
        showErrorAlert('Server error. Please try again later.');
      } else {
        showErrorAlert(error.message || 'An error occurred while creating the category');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSelectedIcon = () => {
    const selectedIcon = icons.find(icon => icon.value === formData.icon);
    return selectedIcon ? selectedIcon.icon : <FaWallet />;
  };

  return (
    <Layout>
      <div className="create-category">
        <div className="cat-page-header">
          <h1>Create Category</h1>
          <p>Add a new transaction category to organize your finances</p>
        </div>

        <form onSubmit={handleSubmit} className="category-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Category Name</label>
              <input
                style={{width: 'auto'}}
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Groceries, Salary, Rent"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="category_type">Category Type</label>
              <select
                style={{width: 'auto', height: '50px'}}
                id="category_type"
                name="category_type"
                value={formData.category_type}
                onChange={handleChange}
              >
                {categoryTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group full-width">
              <label htmlFor="description">Description</label>
              <textarea
                style={{width: 'auto'}}
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Describe this category..."
                className={errors.description ? 'error' : ''}
                rows="3"
              />
              {errors.description && <span className="error-message">{errors.description}</span>}
            </div>

            <div className="form-group">
              <label>Icon</label>
              <div className="icon-selector">
                <button
                  type="button"
                  className="icon-preview"
                  onClick={() => setShowIconPicker(!showIconPicker)}
                >
                  <span className="selected-icon">{getSelectedIcon()}</span>
                  <span className="icon-label">Select Icon</span>
                </button>
                {showIconPicker && (
                  <div className="icon-picker">
                    <div className="icon-grid">
                      {icons.map(({ value, icon, label }) => (
                        <button
                          key={value}
                          type="button"
                          className={`icon-option ${formData.icon === value ? 'selected' : ''}`}
                          onClick={() => handleIconSelect(value)}
                          title={label}
                        >
                          {icon}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Color</label>
              <div className="color-selector">
                <button
                  type="button"
                  className="color-preview"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  style={{ backgroundColor: formData.color }}
                />
                {showColorPicker && (
                  <div className="color-picker">
                    <div className="color-picker-header">
                      <span>Select Color</span>
                      <button
                        type="button"
                        className="close-picker"
                        onClick={() => setShowColorPicker(false)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                    <HexColorPicker color={formData.color} onChange={handleColorChange} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-secondary" 
              onClick={() => navigate('/transaction-categories')}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="cat-btn-primary"
              disabled={isSubmitting }
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="spinner" /> Creating...
                </>
              ) : (
                <>
                  <FaSave /> Create Category
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateTransactionCategory;
